// Iter G (iter148) — Page-level Sizing Coach ribbon.
// A single ribbon at the top of Wheel Strategies (Mode B) that bundles
// sizing insights the user should act on before committing more capital:
//   - Basket-scale suggestion (mirrors what SuggestBasketModal computed
//     last, if available)
//   - Portfolio-level capital-usage warning if the current SELECTED set
//     already exceeds 40% NetLiq (soft), 50% (hard cap)
//   - Regime-fit warning if a majority of selected rows are 'against'
//     current regime
//
// Per the user's explicit instruction: NO row-level optimizer chips —
// this is the ONLY sizing-coach surface at the page level.
import React from "react";

function _fmtRibbonAge(sec) {
  if (sec == null) return "just now";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${Math.round(sec / 3600)}h`;
}

export function SizingCoachRibbon({ selectedRows = [], netliq = 100000, riskBudget = null, lastBasketCoach = null }) {
  // Iter149 Mini-Spec Part 3 — denominator upgrade: when a risk-budget
  // block is provided, thresholds run against `available_for_new_trades`
  // (stressed) rather than NetLiq. Same 40%/50% soft/hard logic,
  // fundamentally more accurate cushion metric.
  const useRiskBudget = riskBudget && (riskBudget.available_for_new_trades || 0) > 0;
  const selectedCapital = selectedRows.reduce(
    (sum, r) => sum + (useRiskBudget ? (r.stressed_margin || r.capital_req || 0) : (r.capital_req || 0)),
    0,
  );
  const denom = useRiskBudget ? riskBudget.available_for_new_trades : netliq;
  const denomLabel = useRiskBudget ? "risk budget available" : "NetLiq";
  const capitalPct = denom > 0 ? (selectedCapital / denom) * 100 : 0;
  const againstCount = selectedRows.filter(r => r.regime_fit === "against").length;
  const totalSelected = selectedRows.length;
  const againstPct = totalSelected > 0 ? (againstCount / totalSelected) * 100 : 0;

  // Priority order — if a hard cap trips, only show that; otherwise stack.
  const items = [];
  if (capitalPct >= 50) {
    items.push({
      kind: "hard-cap",
      icon: "🔴",
      msg: `Hard cap reached: ${capitalPct.toFixed(1)}% of ${denomLabel} deployed. Remove positions before adding new ones.`,
    });
  } else if (capitalPct >= 40) {
    items.push({
      kind: "soft-cap",
      icon: "🟡",
      msg: `Approaching cap: ${capitalPct.toFixed(1)}% of ${denomLabel} deployed (soft limit 40%, hard 50%).`,
    });
  }
  if (againstPct >= 50 && totalSelected >= 2) {
    items.push({
      kind: "regime-mix",
      icon: "⚡",
      msg: `${againstCount} of ${totalSelected} selected rows are against current regime. Consider dropping them or accepting the risk consciously.`,
    });
  }
  if (lastBasketCoach) {
    items.push({
      kind: "basket-coach",
      icon: "💡",
      msg: `Sizing Coach suggests: ${lastBasketCoach.reason}. Switch profile at your discretion.`,
    });
  }
  // Iter149: staleness warning on the ribbon.
  if (useRiskBudget && riskBudget.staleness_state === "stale") {
    items.push({
      kind: "soft-cap",  // amber styling
      icon: "⏱",
      msg: `Account data is > 30 min old (as of ${_fmtRibbonAge(riskBudget.staleness_age_sec)} ago). New suggestions still allowed — refresh your positions for accurate risk sizing.`,
    });
  }

  if (items.length === 0) return null;

  const _bgFor = (kind) =>
    kind === "hard-cap" ? "rgba(239,68,68,0.08)"
    : kind === "soft-cap" ? "rgba(245,158,11,0.08)"
    : "rgba(59,130,246,0.06)";
  const _borderFor = (kind) =>
    kind === "hard-cap" ? "rgba(239,68,68,0.35)"
    : kind === "soft-cap" ? "rgba(245,158,11,0.3)"
    : "rgba(59,130,246,0.25)";
  const _textFor = (kind) =>
    kind === "hard-cap" ? "#F87171"
    : kind === "soft-cap" ? "#F59E0B"
    : "#60A5FA";

  return (
    <div className="space-y-1" data-testid="sizing-coach-ribbon">
      {items.map((it, i) => (
        <div key={i} className="px-3 py-1.5 rounded-sm border text-[11px] flex items-center gap-2"
          style={{ background: _bgFor(it.kind), borderColor: _borderFor(it.kind), color: _textFor(it.kind) }}
          data-testid={`sizing-coach-item-${it.kind}`}>
          <span>{it.icon}</span>
          <span className="font-bold">Sizing Coach</span>
          <span style={{ color: "var(--hoip-text-primary)" }}>{it.msg}</span>
        </div>
      ))}
    </div>
  );
}
